<template>
  <a-card class="filter">
    <div class="filter__list">
      <div class="filter__item">
        <a-flex align="center" gap="middle">
          <a-input
            :placeholder="searchPlaceholder"
            enter-button
            v-model:value="searchLocal"
            :allowClear="true"
          />
          <a-popover
            v-model:open="popoverFilterIsVisible"
            trigger="click"
            placement="bottomRight"
            :arrow="false"
          >
            <template #content>
              <div class="list-filter">
                <template v-if="partnerStore.hasLeastOneIntegration">
                  <!-- Фильтр "Созданы в" -->
                  <div class="filter__item _markets">
                    <a-typography-title
                      :level="titleLevel"
                      class="filter__item-title"
                    >
                      Созданы в:
                    </a-typography-title>
                    <markets-checkbox
                      v-model:selectedMarkets="createdInMarkets"
                      :type="'onlyIcon'"
                    />
                  </div>

                  <!-- Фильтр "Не созданы в" -->
                  <div class="filter__item _markets">
                    <a-typography-title
                      :level="titleLevel"
                      class="filter__item-title"
                    >
                      Не созданы в:
                    </a-typography-title>
                    <markets-checkbox
                      v-model:selectedMarkets="notCreatedInMarkets"
                      :type="'onlyIcon'"
                    />
                  </div>
                </template>
                <div class="filter__item _markets">
                  <popover-menu main-button-text="Out of stock">
                    <a-radio-group
                      v-model:value="outOfStock"
                      size="large"
                      buttonStyle="solid"
                    >
                      <a-flex vertical gap="middle">
                        <a-radio class="flex-reverse-radio" :value="'fbo'">
                          Без остатков FBO
                        </a-radio>
                        <a-radio class="flex-reverse-radio" :value="'fbs'">
                          Без остатков FBS
                        </a-radio>
                        <a-radio class="flex-reverse-radio" :value="'both'">
                          Без остатков FBO и FBS
                        </a-radio>
                      </a-flex>
                    </a-radio-group>
                  </popover-menu>
                  <filter-button v-model="readyToExport">
                    Экспорт
                  </filter-button>
                </div>

                <div
                  class="filter__item _markets"
                  style="justify-content: flex-end"
                >
                  <a-button
                    type="primary"
                    :disabled="clearBtnIsDisabled"
                    @click="clearFilter"
                  >
                    Сбросить фильтры
                  </a-button>
                </div>
              </div>
            </template>

            <a-badge :count="activeFilterCount">
              <a-button
                style="
                  display: flex;
                  padding: 0 12px;
                  justify-content: center;
                  align-items: center;
                  border: 1px solid var(--color-primary-text);
                "
              >
                <img src="@/assets/images/icons/filter.svg" alt="filter" />
              </a-button>
            </a-badge>
          </a-popover>
          <slot></slot>
          <columns-action
            :disabled="disabledColumnsBtn"
            v-model:dragList="dragList"
            :initList="columnForInit"
            :tableName="tableName"
          />
        </a-flex>
      </div>
    </div>
  </a-card>
</template>

<script setup lang="ts">
import { computed, defineEmits, ref, watch } from "vue";
import MarketsCheckbox from "@/components/UI/MarketsCheckbox/MarketsCheckbox.vue";
import { debounce } from "@/tools/debounce";
import FilterButton from "@/components/product/products/FilterButton.vue";
import { usePartner } from "@/stores/partner/partner";
import ColumnsAction from "@/components/base/table/ColumnsAction.vue";
import { MarketPlacesEnum } from "@/types/types";
import type { TableColumnTypeCustom } from "@/types/claims";
import PopoverMenu from "@/components/UI/PopoverMenu.vue";

const createdInOzon = defineModel<boolean | null>("createdInOzon", {
  required: true,
});
const createdInWb = defineModel<boolean | null>("createdInWb", {
  required: true,
});
const notCreatedInOzon = defineModel<boolean | null | undefined>(
  "notCreatedInOzon",
  {
    required: true,
  },
);
const notCreatedInWb = defineModel<boolean | null | undefined>(
  "notCreatedInWb",
  {
    required: true,
  },
);

const remainsFbsAndFbo = defineModel<boolean | null>("remainsFbsAndFbo");
const readyToExport = defineModel<boolean | null>("readyToExport");
const dragList = defineModel<TableColumnTypeCustom[]>("dragList", {
  required: true,
});
const columnForInit = defineModel<TableColumnTypeCustom[]>("columnForInit", {
  required: true,
});
const tableName = defineModel<string>("tableName", { required: true });

const outOfStockFBO = defineModel<any>("outOfStockFBO");
const outOfStockFBS = defineModel<any>("outOfStockFBS");

const props = defineProps<{
  search: string;
  disabledColumnsBtn?: boolean;
  searchPlaceholder?: string;
}>();

const emit = defineEmits<{
  "update:search": [value: string];
  filterCleaned: [];
}>();

const partnerStore = usePartner();
const searchLocal = ref(props.search);
const titleLevel = 5;
const popoverFilterIsVisible = ref<boolean>(false);

const createdInMarkets = ref<MarketPlacesEnum[]>([]);
const notCreatedInMarkets = ref<MarketPlacesEnum[]>([]);
const outOfStock = ref<"fbo" | "fbs" | "both" | null>(null);

const activeFilterCount = computed(() => {
  let count = 0;

  if (createdInMarkets.value?.length) {
    count++;
  }

  if (notCreatedInMarkets.value?.length) {
    count++;
  }

  if (readyToExport.value) {
    count++;
  }

  if (outOfStock.value) {
    count++;
  }

  return count;
});

const clearBtnIsDisabled = computed(() => {
  return !activeFilterCount.value;
});

const clearFilter = () => {
  createdInMarkets.value = [];
  notCreatedInMarkets.value = [];
  remainsFbsAndFbo.value = false;
  readyToExport.value = false;
  outOfStock.value = null;

  emit("filterCleaned");
};

const debouncedSearch = (newSearch: string) => {
  emit("update:search", newSearch);
};

const handleSearchChange = debounce((newSearch: string) => {
  debouncedSearch(newSearch);
}, 400);

watch(
  () => searchLocal.value,
  (newValue) => {
    handleSearchChange(newValue);
  },
);

watch(
  () => createdInMarkets.value,
  (newValue, oldValue) => {
    createdInWb.value = newValue.includes(MarketPlacesEnum.wildberries);
    createdInOzon.value = newValue.includes(MarketPlacesEnum.ozon);

    const marketsToRemove = newValue.filter(
      (market) => !oldValue.includes(market),
    );
    if (marketsToRemove.length > 0) {
      notCreatedInMarkets.value = notCreatedInMarkets.value.filter(
        (market) => !marketsToRemove.includes(market),
      );
    }
  },
  { deep: true },
);

watch(
  () => notCreatedInMarkets.value,
  (newValue, oldValue) => {
    notCreatedInWb.value = newValue.includes(MarketPlacesEnum.wildberries);
    notCreatedInOzon.value = newValue.includes(MarketPlacesEnum.ozon);

    const marketsToRemove = newValue.filter(
      (market) => !oldValue.includes(market),
    );
    if (marketsToRemove.length > 0) {
      createdInMarkets.value = createdInMarkets.value.filter(
        (market) => !marketsToRemove.includes(market),
      );
    }
  },
  { deep: true },
);

watch(
  [createdInWb, createdInOzon],
  ([wb, ozon]) => {
    const newMarkets: MarketPlacesEnum[] = [];
    if (wb) newMarkets.push(MarketPlacesEnum.wildberries);
    if (ozon) newMarkets.push(MarketPlacesEnum.ozon);
    if (
      JSON.stringify(createdInMarkets.value.sort()) !==
      JSON.stringify(newMarkets.sort())
    ) {
      createdInMarkets.value = newMarkets;
    }
  },
  { immediate: true },
);

watch(
  [notCreatedInWb, notCreatedInOzon],
  ([wb, ozon]) => {
    const newMarkets: MarketPlacesEnum[] = [];
    if (wb) newMarkets.push(MarketPlacesEnum.wildberries);
    if (ozon) newMarkets.push(MarketPlacesEnum.ozon);
    if (
      JSON.stringify(notCreatedInMarkets.value.sort()) !==
      JSON.stringify(newMarkets.sort())
    ) {
      notCreatedInMarkets.value = newMarkets;
    }
  },
  { immediate: true },
);

watch(
  [outOfStockFBO, outOfStockFBS],
  ([fbo, fbs]) => {
    if (fbo && fbs) {
      outOfStock.value = "both";
    } else if (fbo) {
      outOfStock.value = "fbo";
    } else if (fbs) {
      outOfStock.value = "fbs";
    } else {
      outOfStock.value = null;
    }
  },
  { immediate: true },
);

watch(
  () => props.search,
  (newValue) => {
    if (searchLocal.value !== newValue) {
      searchLocal.value = newValue;
    }
  },
  { immediate: true },
);

watch(
  () => outOfStock.value,
  (newValue, oldValue) => {
    outOfStockFBO.value =
      newValue === "fbo" || newValue === "both" ? true : null;
    outOfStockFBS.value =
      newValue === "fbs" || newValue === "both" ? true : null;
  },
  { deep: true },
);
</script>

<style scoped lang="scss">
.filter__list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.filter__item {
  &._markets {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;

    .filter__item-title {
      min-width: 117px;
    }
  }
}

.list-filter {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
